/**
 * SimulationManager Component
 *
 * Admin UI for creating and managing exam simulation configurations.
 *
 * Responsibilities:
 * - Load and display existing simulation configurations
 * - Provide form to create/update configurations (level, exam type, module order, timings)
 * - Allow per-module section configuration with optional titles and number of questions
 * - Persist changes via REST endpoints
 * - Compute and display derived total duration based on module timings and pauses
 *
 * Data model:
 * - SectionTitle: backend-provided records used as optional titles per module/section
 * - ModuleSettings: per-module configuration (order of sections, optional titles, question counts)
 * - SimulationSettings: full configuration for a level+exam type pair
 *
 * @module SimulationManager
 */

import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Save,
  Edit,
  Trash2,
  Clock,
  Settings,
  CheckCircle,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getSimulationSettings,
  createSimulationSettings,
  updateSimulationSettings,
  deleteSimulationSettings,
  type SimulationSettings,
} from '@/lib/simulationApi';
import { getSectionTitlesList, type SectionTitle } from '@/lib/sectionTitleApi';
import { CEFR_LEVELS, EXAM_TYPES } from '@/lib/constants';
import { logger } from '@/lib/logger';
import {
  buildDefaultSimulationSettings,
  calculateSimulationTotalDuration,
  normalizeModuleTimings,
  normalizeSectionSettings,
  normalizeSimulationSettings,
  SIMULATION_MODULES,
  toggleSimulationModule,
} from './simulationManagerUtils';

const LEVELS = CEFR_LEVELS;
const EXAM_TYPES_ARRAY = EXAM_TYPES.map((t) => t.value);
const MODULES_ARRAY = SIMULATION_MODULES;

/**
 * SimulationManager Component
 *
 * Root admin component for creating and managing exam simulation configurations.
 * Handles CRUD operations for simulation settings with per-module section configuration.
 *
 * @returns {JSX.Element} Simulation configuration management interface
 */
export default function SimulationManager() {
  const queryClient = useQueryClient();

  /** Simulation list fetched and cached via React Query */
  const { data: simulations = [], isLoading } = useQuery({
    queryKey: ['admin-simulations'],
    queryFn: getSimulationSettings,
  });

  /** Section titles fetched once and cached */
  const { data: sectionTitles = [] } = useQuery({
    queryKey: ['admin-section-titles'],
    queryFn: getSectionTitlesList,
  });

  /** State for currently selected simulation for viewing */
  const [selectedSimulation, setSelectedSimulation] =
    useState<SimulationSettings | null>(null);

  /** State for edit mode toggle */
  const [isEditing, setIsEditing] = useState(false);

  /** Loading state for save mutation */
  const [saving, setSaving] = useState(false);

  /** State for delete confirmation dialog */
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [simulationToDelete, setSimulationToDelete] =
    useState<SimulationSettings | null>(null);
  const [deleting, setDeleting] = useState(false);

  /**
   * State for success/error messages
   */
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  /**
   * Form state for simulation configuration being edited/created
   * Initialized with default values for all modules to prevent undefined access.
   */
  const [formData, setFormData] = useState<SimulationSettings>(
    buildDefaultSimulationSettings,
  );

  /**
   * Recalculate derived total duration whenever timings or pause/module count changes.
   */
  useEffect(() => {
    const calculatedTotal = calculateSimulationTotalDuration(
      formData.moduleOrder,
      formData.moduleTimings,
      formData.pauseBetweenModules,
    );

    setFormData((prev) =>
      prev.totalDuration === calculatedTotal
        ? prev
        : { ...prev, totalDuration: calculatedTotal },
    );
  }, [
    formData.moduleTimings,
    formData.pauseBetweenModules,
    formData.moduleOrder,
  ]);

  /**
   * Set a timed, dismissible message banner.
   */
  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
    if (type === 'success') {
      toast.success(text);
    } else {
      toast.error(text);
    }
  };

  /**
   * Create or update a simulation configuration.
   * Ensures sectionSettings objects contain required maps for backward compatibility.
   */
  const handleSave = async () => {
    try {
      setSaving(true);

      const moduleTimings = normalizeModuleTimings(formData.moduleTimings);
      const cleanedFormData = normalizeSimulationSettings({
        ...formData,
        moduleTimings,
        sectionSettings: normalizeSectionSettings(formData.sectionSettings),
        totalDuration: calculateSimulationTotalDuration(
          formData.moduleOrder,
          moduleTimings,
          formData.pauseBetweenModules,
        ),
      });

      if (selectedSimulation?.id) {
        await updateSimulationSettings(selectedSimulation.id, cleanedFormData);
        showMessage('success', 'Simulation configuration updated successfully');
      } else {
        await createSimulationSettings(cleanedFormData);
        showMessage('success', 'Simulation configuration created successfully');
      }
      await queryClient.invalidateQueries({ queryKey: ['admin-simulations'] });
      resetForm();
    } catch (error: any) {
      logger.error('Error saving simulation', error);
      showMessage(
        'error',
        error.message || 'Failed to save simulation configuration',
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Put the selected simulation into edit mode, normalizing missing properties.
   */
  const handleEdit = (simulation: SimulationSettings) => {
    setSelectedSimulation(simulation);
    setFormData(normalizeSimulationSettings(simulation));
    setIsEditing(true);
  };

  /**
   * Open delete confirmation dialog for a simulation
   */
  const openDeleteDialog = (simulation: SimulationSettings) => {
    setSimulationToDelete(simulation);
    setShowDeleteDialog(true);
  };

  /**
   * Delete a simulation configuration (with confirmation).
   */
  const handleDelete = async () => {
    if (!simulationToDelete?.id) return;

    try {
      setDeleting(true);
      await deleteSimulationSettings(simulationToDelete.id);
      showMessage('success', 'Simulation configuration deleted successfully');
      setShowDeleteDialog(false);
      setSimulationToDelete(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-simulations'] });
    } catch (error: any) {
      logger.error('Error deleting simulation', error);
      showMessage(
        'error',
        error.message || 'Failed to delete simulation configuration',
      );
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Reset the form to default, fully-initialized state and exit edit mode.
   */
  const resetForm = () => {
    setFormData(buildDefaultSimulationSettings());
    setSelectedSimulation(null);
    setIsEditing(false);
  };

  /**
   * Reorder a module up/down within the moduleOrder list.
   */
  const moveModule = (index: number, direction: 'up' | 'down') => {
    if (index < 0) {
      return;
    }

    const newOrder = [...formData.moduleOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      [newOrder[index], newOrder[targetIndex]] = [
        newOrder[targetIndex],
        newOrder[index],
      ];
      setFormData((prev) => ({ ...prev, moduleOrder: newOrder }));
    }
  };

  /**
   * Include or exclude a module from the saved simulation flow.
   */
  const handleModuleEnabledChange = (module: string, enabled: boolean) => {
    setFormData((prev) => {
      const moduleOrder = toggleSimulationModule(
        module,
        enabled,
        prev.moduleOrder,
      );
      return {
        ...prev,
        moduleOrder,
        moduleTimings: normalizeModuleTimings(prev.moduleTimings),
        sectionSettings: normalizeSectionSettings(prev.sectionSettings),
      };
    });
  };

  // Helper: Create module action handlers
  const createModuleHandlers = (index: number) => {
    return {
      handleMoveUp: () => moveModule(index, 'up'),
      handleMoveDown: () => moveModule(index, 'down'),
    };
  };

  // Helper: Handle level change
  const handleLevelChange = (value: string) => {
    setFormData((prev) => ({ ...prev, level: value }));
  };

  // Helper: Handle exam type change
  const handleExamTypeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, examType: value }));
  };

  // Helper: Handle active status change
  const handleActiveChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, isActive: checked }));
  };

  // Helper: Create module timing change handler
  const createModuleTimingHandler = (module: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        moduleTimings: {
          ...prev.moduleTimings,
          [module]: Number.parseInt(e.target.value) || 0,
        },
      }));
    };
  };

  // Helper: Handle pause time change
  const handlePauseTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      pauseBetweenModules: Number.parseInt(e.target.value) || 0,
    }));
  };

  // Helper: Render level options for select
  const renderLevelOptions = () => {
    return LEVELS.map((level) => (
      <SelectItem key={level} value={level}>
        <span className="font-ui">{level}</span>
      </SelectItem>
    ));
  };

  // Helper: Render exam type options for select
  const renderExamTypeOptions = () => {
    return EXAM_TYPES_ARRAY.map((type) => (
      <SelectItem key={type} value={type}>
        <span className="font-ui">{type.toUpperCase()}</span>
      </SelectItem>
    ));
  };

  // Helper: Render simulation list content
  const renderSimulationListContent = () => {
    if (isLoading && simulations.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground font-ui">
          <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      );
    }
    if (simulations.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground font-ui">
          No configurations found
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {simulations.map((simulation) => (
          <Card key={simulation.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold font-ui">
                    {simulation.level} {simulation.examType.toUpperCase()}
                  </h3>
                  {!simulation.isActive && (
                    <Badge variant="secondary" className="font-ui">
                      Inactive
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground font-ui">
                  <span>
                    {simulation.level} • {simulation.examType.toUpperCase()}
                  </span>
                  <span>{simulation.moduleOrder.length} modules</span>
                  <span>{simulation.totalDuration} min</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(simulation)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => openDeleteDialog(simulation)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  // Helper: Create section action handlers
  const createSectionHandlers = (
    module: string,
    section: number,
    sectionIndex: number,
  ) => {
    return {
      handleMoveUp: () => moveSection(module, sectionIndex, 'up'),
      handleMoveDown: () => moveSection(module, sectionIndex, 'down'),
      handleRemove: () => removeSection(module, sectionIndex),
      handleTitleChange: (value: string) =>
        updateSectionTitle(module, section, value),
      handleQuestionsChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSectionQuestions(
          module,
          section,
          Number.parseInt(e.target.value) || 0,
        );
      },
    };
  };

  // Helper: Render available titles for select dropdown
  const renderAvailableTitles = (module: string, section: number) => {
    try {
      const availableTitles = getAvailableTitles(module, section);
      if (availableTitles && availableTitles.length > 0) {
        return availableTitles.map((title) => (
          <SelectItem key={title.id} value={title.text}>
            <span className="font-ui">
              {title.text.length > 60
                ? `${title.text.substring(0, 60)}...`
                : title.text}
            </span>
          </SelectItem>
        ));
      }
      return (
        <div className="px-2 py-1.5 text-sm text-muted-foreground font-ui">
          No titles available - you can proceed without selecting a title
        </div>
      );
    } catch (error) {
      logger.error('Error rendering dropdown', error);
      return (
        <div className="px-2 py-1.5 text-sm text-muted-foreground font-ui">
          Error loading titles - you can proceed without selecting
        </div>
      );
    }
  };

  /**
   * Reorder a section within a module's `order` array.
   */
  const moveSection = (
    module: string,
    sectionIndex: number,
    direction: 'up' | 'down',
  ) => {
    const moduleSettings = { ...formData.sectionSettings[module] };
    const newOrder = [...moduleSettings.order];
    const targetIndex =
      direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;

    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      [newOrder[sectionIndex], newOrder[targetIndex]] = [
        newOrder[targetIndex],
        newOrder[sectionIndex],
      ];
      moduleSettings.order = newOrder;

      setFormData((prev) => ({
        ...prev,
        sectionSettings: { ...prev.sectionSettings, [module]: moduleSettings },
      }));
    }
  };

  /**
   * Append a new section to a module, assigning the next available numeric index.
   */
  const addSection = (module: string) => {
    try {
      const moduleSettings = formData.sectionSettings[module]
        ? { ...formData.sectionSettings[module] }
        : { order: [1], titles: {}, questions: {} };
      const maxSection =
        moduleSettings.order?.length > 0
          ? Math.max(...moduleSettings.order, 0)
          : 0;
      moduleSettings.order.push(maxSection + 1);

      setFormData((prev) => ({
        ...prev,
        sectionSettings: { ...prev.sectionSettings, [module]: moduleSettings },
      }));
    } catch (error) {
      logger.error('Error adding section', error);
    }
  };

  /**
   * Remove a section entry at a given index from a module.
   */
  const removeSection = (module: string, sectionIndex: number) => {
    try {
      const moduleSettings = formData.sectionSettings[module]
        ? { ...formData.sectionSettings[module] }
        : { order: [1], titles: {}, questions: {} };
      if (moduleSettings.order?.length > sectionIndex) {
        moduleSettings.order.splice(sectionIndex, 1);

        setFormData((prev) => ({
          ...prev,
          sectionSettings: {
            ...prev.sectionSettings,
            [module]: moduleSettings,
          },
        }));
      }
    } catch (error) {
      logger.error('Error removing section', error);
    }
  };

  /**
   * Assign or clear a section title for a given module/section.
   */
  const updateSectionTitle = (
    module: string,
    section: number,
    title: string,
  ) => {
    try {
      const moduleSettings = { ...formData.sectionSettings[module] };
      if (!moduleSettings.titles) {
        moduleSettings.titles = {};
      }

      // Allow empty titles
      moduleSettings.titles = {
        ...moduleSettings.titles,
        [section]: title || '',
      };

      setFormData((prev) => ({
        ...prev,
        sectionSettings: { ...prev.sectionSettings, [module]: moduleSettings },
      }));
    } catch (error) {
      logger.error('Error updating section title', error);
    }
  };

  /**
   * Set the number of questions for a given module/section.
   */
  const updateSectionQuestions = (
    module: string,
    section: number,
    questions: number,
  ) => {
    const moduleSettings = { ...formData.sectionSettings[module] };
    // Ensure questions object exists
    if (!moduleSettings.questions) {
      moduleSettings.questions = {};
    }
    moduleSettings.questions = {
      ...moduleSettings.questions,
      [section]: questions,
    };

    setFormData((prev) => ({
      ...prev,
      sectionSettings: { ...prev.sectionSettings, [module]: moduleSettings },
    }));
  };

  /**
   * Retrieve titles filtered by current level, module and section number.
   * Returns an empty list if source data is missing/invalid.
   */
  const getAvailableTitles = (module: string, section: number) => {
    try {
      logger.debug('Getting titles', {
        level: formData.level,
        module,
        section,
        availableCount: sectionTitles?.length || 0,
      });

      // Handle edge cases
      if (
        !sectionTitles ||
        !Array.isArray(sectionTitles) ||
        sectionTitles.length === 0
      ) {
        logger.debug('No section titles available or invalid data');
        return [];
      }

      if (
        !formData.level ||
        !module ||
        section === undefined ||
        section === null
      ) {
        logger.debug('Invalid filter parameters');
        return [];
      }

      const filtered = sectionTitles.filter((title) => {
        if (!title || typeof title !== 'object') return false;
        return (
          title.level === formData.level &&
          title.module === module &&
          title.section === section
        );
      });

      logger.debug('Filtered results', {
        count: filtered.length,
        titles: filtered,
      });
      return filtered;
    } catch (error) {
      logger.error('Error in getAvailableTitles', error);
      return [];
    }
  };

  // Helper: Render sections tab content
  const renderSectionsTabContent = () => {
    try {
      return (
        <>
          <div className="p-3 bg-muted/50 rounded-lg mb-4">
            <p className="text-sm text-muted-foreground font-ui">
              Debug: {sectionTitles?.length || 0} section titles loaded for
              level {formData.level}
            </p>
            {sectionTitles?.length > 0 && (
              <div className="text-xs mt-1 font-ui">
                Available:{' '}
                {sectionTitles
                  .map((t) => `${t.module}-${t.section}`)
                  .join(', ')}
              </div>
            )}
            <div className="text-xs mt-1 text-primary font-ui">
              Max sections per module:{' '}
              {['reading', 'listening', 'writing', 'speaking']
                .map(
                  (module) => `${module}(${getMaxSectionsForModule(module)})`,
                )
                .join(', ')}
            </div>
          </div>
          {formData.moduleOrder?.map((module) => {
            // Ensure module has section settings
            const moduleSettings = formData.sectionSettings[module] || {
              order: [1],
              titles: {},
              questions: {},
            };

            return (
              <Card key={module}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg capitalize font-ui">
                    {module}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {moduleSettings.order?.map((section, sectionIndex) => {
                    const {
                      handleMoveUp,
                      handleMoveDown,
                      handleRemove,
                      handleTitleChange,
                      handleQuestionsChange,
                    } = createSectionHandlers(module, section, sectionIndex);

                    return (
                      <div
                        key={`${module}-${section}`}
                        className="p-3 bg-muted/50 rounded-lg space-y-3"
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="font-ui">
                            Section {sectionIndex + 1}
                          </Badge>
                          <div className="flex gap-1 ml-auto">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleMoveUp}
                              disabled={sectionIndex === 0}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleMoveDown}
                              disabled={
                                sectionIndex === moduleSettings.order.length - 1
                              }
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleRemove}
                              disabled={moduleSettings.order.length <= 1}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <Label className="text-sm font-ui">
                              Section Title
                            </Label>
                            <Select
                              value={moduleSettings.titles[section] || ''}
                              onValueChange={handleTitleChange}
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={`Select title for section ${section}`}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {renderAvailableTitles(module, section)}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label className="text-sm font-ui">
                              Number of Questions
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={moduleSettings.questions?.[section] || ''}
                              onChange={handleQuestionsChange}
                              placeholder="Enter number of questions (optional)"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addSection(module)}
                    className="w-full"
                    disabled={
                      moduleSettings.order.length >=
                      getMaxSectionsForModule(module)
                    }
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    <span className="font-ui">
                      {moduleSettings.order.length >=
                      getMaxSectionsForModule(module)
                        ? `Max sections reached (${getMaxSectionsForModule(module)})`
                        : 'Add Section'}
                    </span>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </>
      );
    } catch (error) {
      logger.error('Error rendering sections tab', error);
      return (
        <div className="p-6 text-center">
          <p className="text-muted-foreground mb-4 font-ui">
            Error rendering sections. Please refresh the page.
          </p>
          <Button
            onClick={() => globalThis.location.reload()}
            variant="outline"
          >
            <span className="font-ui">Refresh Page</span>
          </Button>
        </div>
      );
    }
  };

  /**
   * Determine the number of unique available sections for a given module at the current level.
   * Used to gate the maximum number of sections that can be added in the UI.
   */
  const getMaxSectionsForModule = (module: string): number => {
    try {
      if (
        !sectionTitles ||
        !Array.isArray(sectionTitles) ||
        sectionTitles.length === 0
      ) {
        return 0;
      }

      if (!formData.level || !module) {
        return 0;
      }

      const availableSections = sectionTitles
        .filter(
          (title) => title.level === formData.level && title.module === module,
        )
        .map((title) => title.section);

      const uniqueSections = [...new Set(availableSections)];
      logger.debug('Max sections for module', {
        level: formData.level,
        module,
        maxSections: uniqueSections.length,
      });
      return uniqueSections.length;
    } catch (error) {
      logger.error('Error in getMaxSectionsForModule', error);
      return 0;
    }
  };

  const activeModuleDuration = formData.moduleOrder.reduce(
    (sum, module) =>
      sum + Math.max(0, Number(formData.moduleTimings[module]) || 0),
    0,
  );
  const activePauseDuration =
    Math.max(0, formData.moduleOrder.length - 1) * formData.pauseBetweenModules;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground font-ui">
            Simulation Configuration Management
          </h2>
          <p className="text-muted-foreground font-ui">
            Create and manage exam simulation configurations with module order,
            timings, and section settings
          </p>
        </div>
        <Button
          onClick={() => setIsEditing(!isEditing)}
          variant={isEditing ? 'outline' : 'default'}
        >
          {isEditing ? (
            <>
              <RotateCcw className="h-4 w-4 mr-2" />
              <span className="font-ui">View Mode</span>
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              <span className="font-ui">Create New</span>
            </>
          )}
        </Button>
      </div>

      {/* Messages */}
      {message && (
        <Alert
          variant={message.type === 'error' ? 'destructive' : 'default'}
          className="font-ui"
        >
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          <AlertDescription className="font-ui">
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configuration Form */}
        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-ui">
                <Settings className="h-5 w-5" />
                {selectedSimulation
                  ? 'Edit Configuration'
                  : 'Create Configuration'}
              </CardTitle>
              <CardDescription className="font-ui">
                Configure simulation settings for a specific level and exam type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic" className="font-ui">
                    Basic
                  </TabsTrigger>
                  <TabsTrigger value="modules" className="font-ui">
                    Modules
                  </TabsTrigger>
                  <TabsTrigger value="sections" className="font-ui">
                    Sections
                  </TabsTrigger>
                  <TabsTrigger value="timing" className="font-ui">
                    Timing
                  </TabsTrigger>
                </TabsList>

                {/* Basic Settings */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2 font-ui">
                      Configuration Information
                    </h4>
                    <p className="text-sm text-muted-foreground font-ui">
                      Configure the basic settings for this simulation. The
                      level and exam type determine which section titles are
                      available for assignment.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="level" className="font-ui">
                        Level
                      </Label>
                      <Select
                        value={formData.level}
                        onValueChange={handleLevelChange}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>{renderLevelOptions()}</SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="examType" className="font-ui">
                        Exam Type
                      </Label>
                      <Select
                        value={formData.examType}
                        onValueChange={handleExamTypeChange}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>{renderExamTypeOptions()}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="font-ui">Active</Label>
                      <p className="text-sm text-muted-foreground font-ui">
                        Enable or disable this simulation configuration
                      </p>
                    </div>
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={handleActiveChange}
                    />
                  </div>
                </TabsContent>

                {/* Module Order */}
                <TabsContent value="modules" className="space-y-4">
                  <div>
                    <Label className="font-ui">Modules</Label>
                    <p className="text-sm text-muted-foreground mb-3 font-ui">
                      Choose the modules included in this configuration and
                      order the active simulation flow.
                    </p>
                    <div className="space-y-2">
                      {MODULES_ARRAY.map((module) => {
                        const activeIndex =
                          formData.moduleOrder.indexOf(module);
                        const isEnabled = activeIndex !== -1;
                        const isLastEnabledModule =
                          isEnabled && formData.moduleOrder.length === 1;
                        const { handleMoveUp, handleMoveDown } =
                          createModuleHandlers(activeIndex);

                        return (
                          <div
                            key={module}
                            className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                              isEnabled ? 'bg-muted' : 'bg-muted/40'
                            }`}
                          >
                            <Badge
                              variant={isEnabled ? 'outline' : 'secondary'}
                              className="w-12 justify-center font-ui"
                            >
                              {isEnabled ? activeIndex + 1 : 'Off'}
                            </Badge>
                            <div className="flex-1">
                              <span
                                className={`capitalize font-medium font-ui ${
                                  isEnabled
                                    ? 'text-foreground'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {module}
                              </span>
                            </div>
                            <Switch
                              checked={isEnabled}
                              disabled={isLastEnabledModule}
                              onCheckedChange={(checked) =>
                                handleModuleEnabledChange(module, checked)
                              }
                              aria-label={`Include ${module} module`}
                            />
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleMoveUp}
                                disabled={!isEnabled || activeIndex === 0}
                                aria-label={`Move ${module} module up`}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleMoveDown}
                                disabled={
                                  !isEnabled ||
                                  activeIndex ===
                                    formData.moduleOrder.length - 1
                                }
                                aria-label={`Move ${module} module down`}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                {/* Section Settings */}
                <TabsContent value="sections" className="space-y-4">
                  {renderSectionsTabContent()}
                </TabsContent>

                {/* Timing Settings */}
                <TabsContent value="timing" className="space-y-4">
                  <div>
                    <Label className="font-ui">Module Timings (minutes)</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {formData.moduleOrder.map((module) => {
                        const handleTimingChange =
                          createModuleTimingHandler(module);
                        return (
                          <div key={module}>
                            <Label
                              htmlFor={`timing-${module}`}
                              className="text-sm capitalize font-ui"
                            >
                              {module}
                            </Label>
                            <Input
                              id={`timing-${module}`}
                              type="number"
                              min="1"
                              value={formData.moduleTimings[module] || 0}
                              onChange={handleTimingChange}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="pauseTime" className="font-ui">
                        Pause Between Modules (minutes)
                      </Label>
                      <Input
                        id="pauseTime"
                        type="number"
                        min="0"
                        value={formData.pauseBetweenModules}
                        onChange={handlePauseTimeChange}
                      />
                    </div>
                  </div>

                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-lg font-semibold font-ui">
                        <Clock className="h-5 w-5" />
                        Total Duration: {formData.totalDuration} minutes
                      </div>
                      <div className="text-sm text-muted-foreground mt-1 font-ui">
                        Modules: {activeModuleDuration} minutes + Pauses:{' '}
                        {activePauseDuration} minutes
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span className="font-ui">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      <span className="font-ui">Save Configuration</span>
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  <span className="font-ui">Cancel</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Simulation List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-ui">Simulation Configurations</CardTitle>
            <CardDescription className="font-ui">
              Manage existing simulation configurations
            </CardDescription>
          </CardHeader>
          <CardContent>{renderSimulationListContent()}</CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">
              Delete Simulation Configuration
            </AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              {simulationToDelete &&
                (() => {
                  const simulationName =
                    simulationToDelete.name ||
                    simulationToDelete.level +
                      ' ' +
                      simulationToDelete.examType;
                  return `Are you sure you want to delete the simulation configuration for "${simulationName}"? This action cannot be undone.`;
                })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  <span className="font-ui">Deleting...</span>
                </>
              ) : (
                <span className="font-ui">Delete</span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
